import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { clientFetch } from './.client/fetch';
import { getAuthBroadcastChannel } from './.client/oauth';
import { setUser } from './.client/sentry';
import type { TokenStats } from './.server/rateLimit';
import { REFERRALS_FEATURE_ENABLED } from './constants';
import { settingsService } from './services/settings-service';
import { chatStore } from './stores/chat';
import { getReferrerId } from './stores/location';
import { userStore } from './stores/user';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { SessionsResponse } from '~/routes/api.sessions';
import { AnalyticsAction, AnalyticsTrackEvent, AnalyticsEventStatus } from '~/types/analytics';
import type { UserData } from '~/types/user';
import { wait, withResolvers } from '~/utils/promises';
import { getTrackingDataToken, sendAdConversions } from '~/utils/tracking-params.client';

type OauthProvider = 'github' | 'google_oauth2' | 'login_password';

const LOGIN_LOCK = 'bolt-login';

export function useLogin() {
  const [loginPending, setLoginPending] = useState(false);

  const login = useCallback(
    async (destination?: string, provider?: OauthProvider, params?: Params, upgrade = false) => {
      if (loginPending) {
        if (import.meta.env.DEV) {
          throw new Error('Why can we attempt a login if there is another one in progress?');
        }

        return false;
      }

      setLoginPending(true);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.UserLoginStarted,
          properties: {
            login_provider: provider,
          },
        },
        isAnonymous: true,
      });

      try {
        await attemptLogin({ destination, provider, params, upgrade });

        settingsService.loadSettings().catch(() => {
          toast.error('Failed to load settings');
        });

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.UserLoginCompleted,
            properties: {
              event_status: AnalyticsEventStatus.Success,
              login_provider: provider,
            },
          },
        });

        return true;
      } catch (error: any) {
        console.error(error);

        toast.error(error?.authError ?? 'Unable to login');

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.UserLoginCompleted,
            properties: {
              event_status: AnalyticsEventStatus.Failure,
              login_provider: provider,
              login_error: error?.authError,
            },
          },
          isAnonymous: true,
        });

        return false;
      } finally {
        setLoginPending(false);
      }
    },
    [loginPending],
  );

  return { pending: loginPending, login };
}

async function attemptLogin({
  destination,
  provider,
  params,
  upgrade = false,
}: {
  destination?: string;
  provider?: OauthProvider;
  params?: Params;
  upgrade?: boolean;
}) {
  if (userStore.loggedIn.get() && !upgrade) {
    if (import.meta.env.DEV) {
      throw new Error('Why can you attempt login if you are already logged in?');
    }

    return;
  }

  const lockDeferred = withResolvers<void>();

  const hasLock = await new Promise<boolean>((resolve) => {
    navigator.locks.request(LOGIN_LOCK, { ifAvailable: true }, (lock) => {
      resolve(lock != null);

      return lock ? lockDeferred.promise : null;
    });
  });

  if (!hasLock) {
    throw { authError: 'There is an ongoing login attempt in a different tab' };
  }

  try {
    await attemptLoginUnsynchronized({ destination, provider, params, upgrade });
  } finally {
    lockDeferred.resolve();
  }
}

async function attemptLoginUnsynchronized({
  destination,
  provider,
  params,
  upgrade,
}: {
  destination?: string;
  provider?: OauthProvider;
  params?: Params;
  upgrade?: boolean;
}) {
  const authorizeUri = await beginAuthFlow(destination, provider, params, upgrade);

  if (destination) {
    window.location.href = authorizeUri;

    // block for a while to allow the navigation to happen
    await wait(3_000);

    return;
  }

  const result = await openAuthTab(authorizeUri);

  if ('error' in result) {
    console.error(result.error);

    throw { authError: 'Authentication failed' };
  }

  const { user } = result.auth.userData;

  userStore.init(user, true);

  setUser(
    user
      ? {
          id: user.id,
          username: user.username,
          email: user.email,
        }
      : null,
  );

  chatStore.tokenStats.set(result.auth.tokenStats);
}

async function beginAuthFlow(destination?: string, provider?: OauthProvider, params?: Params, upgrade = false) {
  const trackingDataToken = getTrackingDataToken();

  sendAdConversions(trackingDataToken).catch(console.error);

  const rid = REFERRALS_FEATURE_ENABLED ? getReferrerId() : undefined;

  const response: SessionsResponse = await clientFetch('/api/sessions', {
    method: 'POST',
    body: JSON.stringify({ start: { destination, rid }, upgrade }),
    headers: {
      'content-type': 'application/json',
    },
  });

  if (response.status === 400) {
    const payload: any = await response.json();

    if (payload?.error === 'logged-in') {
      // stopgap solution if you try to login in an old tab
      window.location.reload();

      // block for a while to allow the reload to happen
      await wait(10_000);

      throw new Error('Already logged in');
    }
  }

  if (!response.ok) {
    throw new Error('No authorize URI');
  }

  const { authorizeUri } = (await response.json()) as { authorizeUri: string };

  const uri = new URL(authorizeUri);

  if (provider) {
    uri.searchParams.set('bolt_oauth_provider', provider);
  }

  for (const [key, value] of Object.entries(params ?? {})) {
    uri.searchParams.set(key, value);
  }

  if (trackingDataToken) {
    uri.searchParams.set('ad_conversions_data_token', trackingDataToken);
  }

  return uri.toString();
}

interface AuthData {
  userData: { user?: UserData };
  tokenStats?: TokenStats;
}

interface Params {
  bolt_oauth_scopes?: string;
}

type AuthMessage =
  | {
      auth: AuthData;
    }
  | {
      error: {
        message?: string;
      };
    };

async function openAuthTab(authorizeUri: string): Promise<AuthMessage> {
  const ctrl = new AbortController();

  const loginPromise = new Promise<AuthMessage>((resolve) => {
    getAuthBroadcastChannel().addEventListener(
      'message',
      (event) => {
        resolve(event.data);
      },
      { once: true, signal: ctrl.signal },
    );
  });

  window.open(authorizeUri, '_blank');

  const timeoutPromise = wait(60_000, null);

  timeoutPromise.then(() => ctrl.abort());

  const callbackUrl = await Promise.race([loginPromise, timeoutPromise]);

  if (callbackUrl === null) {
    throw { error: { message: 'Login attempt took too long' } };
  }

  return callbackUrl;
}
